import React, { useState } from 'react';

import { Button, Modal, LoadingButton } from '@common/index';
import { Stack, TextField } from '@mui/material';
import { AutoAwesomeMotionOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { flowActions } from '@store/slices';
import { API_BASE_NO_API, AppDispatch, State } from '@store/index';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createService } from '@requests/service';
import { getServices } from '@store/async/service';

export const CreateService: React.FC = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const { workflow_id } = useParams();

  const [name, setName] = useState('');

  const service_path = useSelector((state: State) => state.tabs.active.service_path);
  const services = useSelector((state: State) => state.flow.services);
  const username = useSelector((state: State) => state.auth.username);
  const open = useSelector((state: State) => state.flow.createService);
  const setOpen = (val: boolean) => dispatch(flowActions.setCreateService(val));

  const convertName = (input: string) => {
    let formatted = input.trim().replace(/ /g, '_');
    formatted = formatted.replace(/[^a-zA-Z0-9_\-]/g, '');
    return formatted.toLowerCase();
  };

  const serviceAlreadyExists = services.some((service) => service.service_id === convertName(name));

  const { mutateAsync: save, status: saveStatus } = useMutation({
    mutationFn: createService,
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['rolesInServices'] });

      if (!data) return;

      if (data?.error) {
        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to create workspace!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      // Track event with Intercom when the workspace is successfully created
      window.Intercom?.('trackEvent', 'flow-workspace-created');

      const service_id = data?.service_id;
      await dispatch(getServices());
      enqueueSnackbar('Workspace created successfully!', { variant: 'success', autoHideDuration: 3000 });

      if (service_id !== convertName(name)) {
        enqueueSnackbar(
          `Your workspace has been given a prefix to keep it unique. It will be referenced by "${service_id}" instead of "${convertName(
            name
          )}" in your execution path.`,
          { variant: 'warning', autoHideDuration: 15000 }
        );
      }

      navigate(`/${service_id}/workflow`);
      setName('');
      setOpen(false);
    },
  });

  const isCreatingService = saveStatus === 'pending';

  const create = () => {
    enqueueSnackbar('Creating workspace...', { variant: 'info', autoHideDuration: 3000 });

    save({
      service_id: convertName(name),
      service_name: name,
      service_path: (service_path || workflow_id) as string,
      user_id: username as string,
    });
  };

  return (
    <Modal
      open={Boolean(open)}
      setOpen={setOpen}
      header='Create a Workspace'
      icon={<AutoAwesomeMotionOutlined sx={{ fontSize: '2.2ch' }} />}
      background='primary'
      info={
        'Workspaces are used to categorize functionality. Each workspace has its own workflows, buckets, permissions, rate limits, and reporting. They are typically used to represent teams, products or domains (NOC, SMS, telnyx.com, etc).'
      }
      data-intercom-target='workspace-modal'
      noBottomBorder
      body={
        <TextField
          variant='standard'
          fullWidth
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          required
          label='Workspace Name'
          value={name}
          placeholder={'A name for your Workspace'}
          error={serviceAlreadyExists}
          helperText={
            serviceAlreadyExists
              ? 'A workspace with this name already exists in your organisation.'
              : name
              ? `Workspace Domain: ${API_BASE_NO_API}/execute/${convertName(name)}`
              : undefined
          }
          onChange={(e) => setName(e.target.value)}
          InputProps={{
            inputProps: {
              'data-intercom-target': 'workspace-name-input-field',
            },
          }}
        />
      }
      actions={
        <Stack direction='row' spacing={1}>
          <Button
            small
            onClick={() => {
              navigate('/');
              setOpen(false);
            }}
            data-intercom-target='workspace-back-button'
          >
            Back
          </Button>
          <LoadingButton
            loading={isCreatingService}
            small
            dataIntercomTarget='workspace-create-button'
            customColor='primary'
            minWidth='70px'
            onClick={create}
            disabled={!name || name.length < 3 || serviceAlreadyExists}
          >
            Create
          </LoadingButton>
        </Stack>
      }
    />
  );
};
